/**
 * The glowing sigil the wand draws while the player gestures.
 *
 * Rendered in camera space on the same plane as the magic circle, so the stroke
 * appears to be traced onto the circle itself. Two passes — a bright core line
 * and a wide soft one — plus rune motes that spark off the leading edge.
 *
 * Drawn as `LineSegments` rather than one continuous `Line`, because a sigil
 * can have several strokes: `breakStroke` already tells the scorer to ignore
 * the sweep between them, and drawing that sweep anyway showed the player a
 * shape the server was never judging.
 */
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { useCameraSpace } from '../camera/useCameraSpace';
import { gestureTrail } from '../gesture/capture';
import { scene as bus } from '../scene/sceneBus';
import { glowTexture } from '../assets/textures';
import { CAST_RIG } from './castRig';

/** Rendered vertices, after decimation. The recorder caps itself at 900. */
const MAX_POINTS = 320;
const MOTES = 40;

export function GestureTrail() {
  const anchor = useRef<THREE.Group>(null);
  const rig = useRef<THREE.Group>(null);
  useCameraSpace(anchor);

  const motes = useRef<THREE.Points>(null);
  const head = useRef<THREE.Sprite>(null);

  const glow = useMemo(() => glowTexture(), []);
  const color = useMemo(() => new THREE.Color('#8fd8ff'), []);

  /** Decimated stroke, reused every frame so the loop never allocates. */
  const path = useMemo(
    () => ({ xy: new Float32Array(MAX_POINTS * 2), stroke: new Int16Array(MAX_POINTS), n: 0 }),
    [],
  );

  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    // Two vertices per segment.
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(MAX_POINTS * 2 * 3), 3));
    g.setDrawRange(0, 0);
    return g;
  }, []);

  // `<line>` in JSX resolves to the SVG element, not THREE.Line, so the two
  // passes are constructed imperatively and mounted with <primitive>.
  const softLine = useMemo(() => {
    const material = new THREE.LineBasicMaterial({
      transparent: true,
      opacity: 0.5,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      toneMapped: false,
    });
    return new THREE.LineSegments(geometry, material);
  }, [geometry]);

  const coreLine = useMemo(() => {
    const material = new THREE.LineBasicMaterial({
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      toneMapped: false,
    });
    const line = new THREE.LineSegments(geometry, material);
    line.position.z = 0.002;
    return line;
  }, [geometry]);

  const moteGeometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(MOTES * 3), 3));
    return g;
  }, []);

  useFrame((state) => {
    const group = rig.current;
    if (!group) return;
    group.position.set(CAST_RIG.x, CAST_RIG.y, CAST_RIG.trailZ);

    const src = gestureTrail.points;
    const active = gestureTrail.active && src.length > 1;
    group.visible = active;
    if (!active) {
      geometry.setDrawRange(0, 0);
      return;
    }

    color.set(gestureTrail.color || bus.castColor);

    // --- decimate to the render budget --------------------------------
    // Every sample of the sigil is published now, so a long stroke can exceed
    // the buffer. Thinning it evenly keeps the whole shape on screen; cropping
    // it — which is what this used to do — hid the half the player drew first.
    const stride = src.length > MAX_POINTS ? Math.ceil(src.length / MAX_POINTS) : 1;
    let n = 0;
    let lastTaken = -1;
    for (let i = 0; i < src.length && n < MAX_POINTS; i += stride) {
      const p = src[i];
      path.xy[n * 2] = p.x * CAST_RIG.trailScale;
      path.xy[n * 2 + 1] = -p.y * CAST_RIG.trailScale;
      path.stroke[n] = p.s;
      lastTaken = i;
      n++;
    }
    // The leading edge is where the eye is; never let decimation lag behind it.
    if (lastTaken >= 0 && lastTaken !== src.length - 1 && n < MAX_POINTS) {
      const p = src[src.length - 1];
      path.xy[n * 2] = p.x * CAST_RIG.trailScale;
      path.xy[n * 2 + 1] = -p.y * CAST_RIG.trailScale;
      path.stroke[n] = p.s;
      n++;
    }
    path.n = n;

    // --- emit one segment per adjacent pair within a stroke -------------
    const attr = geometry.getAttribute('position') as THREE.BufferAttribute;
    const arr = attr.array as Float32Array;
    let v = 0;
    for (let i = 1; i < n; i++) {
      if (path.stroke[i] !== path.stroke[i - 1]) continue; // pen lift
      arr[v * 3] = path.xy[(i - 1) * 2];
      arr[v * 3 + 1] = path.xy[(i - 1) * 2 + 1];
      arr[v * 3 + 2] = 0;
      v++;
      arr[v * 3] = path.xy[i * 2];
      arr[v * 3 + 1] = path.xy[i * 2 + 1];
      arr[v * 3 + 2] = 0;
      v++;
    }
    attr.needsUpdate = true;
    geometry.setDrawRange(0, v);
    geometry.computeBoundingSphere();

    for (const line of [coreLine, softLine]) {
      (line.material as THREE.LineBasicMaterial).color.copy(color);
    }

    // Head spark at the live end of the stroke.
    if (head.current && n > 0) {
      head.current.position.set(path.xy[(n - 1) * 2], path.xy[(n - 1) * 2 + 1], 0.01);
      const mat = head.current.material as THREE.SpriteMaterial;
      mat.color.copy(color);
      mat.opacity = 0.95;
      head.current.scale.setScalar(0.06 + Math.sin(state.clock.elapsedTime * 18) * 0.01);
    }

    // Motes scattered back along the stroke. With no line width to give, this
    // is what carries the stroke's apparent thickness, so they are spread a
    // little wider than the line itself.
    if (motes.current) {
      const mAttr = motes.current.geometry.getAttribute('position') as THREE.BufferAttribute;
      const mArr = mAttr.array as Float32Array;
      for (let i = 0; i < MOTES; i++) {
        const idx = Math.max(0, n - 1 - Math.floor((i / MOTES) * n));
        const jitter = Math.sin(state.clock.elapsedTime * 6 + i * 1.7) * 0.012;
        mArr[i * 3] = path.xy[idx * 2] + jitter;
        mArr[i * 3 + 1] = path.xy[idx * 2 + 1] + Math.cos(state.clock.elapsedTime * 5 + i) * 0.012;
        mArr[i * 3 + 2] = 0.005;
      }
      mAttr.needsUpdate = true;
      (motes.current.material as THREE.PointsMaterial).color.copy(color);
    }
  });

  return (
    <group ref={anchor}>
      <group ref={rig} visible={false}>
      <primitive object={softLine} />
      <primitive object={coreLine} />
      <points ref={motes} geometry={moteGeometry}>
        <pointsMaterial
          size={0.026}
          map={glow}
          transparent
          opacity={0.8}
          sizeAttenuation
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          depthTest={false}
          toneMapped={false}
        />
      </points>
      <sprite ref={head} scale={[0.1, 0.1, 0.1]}>
        <spriteMaterial
          map={glow}
          transparent
          opacity={0}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          depthTest={false}
          toneMapped={false}
        />
        </sprite>
      </group>
    </group>
  );
}
